import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import { useKindeAuth } from '@kinde-oss/kinde-auth-react'
import LoadingScreen from '../components/LoadingScreen/LoadingScreen.jsx'
import { authBridge } from '../lib/AuthBridge.js'
import { useAuthStore } from '../stores/authStore.js'

/**
 * AuthGate — the ONLY place the authentication decision is made.
 *
 * Holds the splash until the Kinde SDK has settled (isLoading false) AND the
 * decision has been synced into authStore, so everything below — layouts,
 * guards, redirects, pages — can trust authStore.isAuthenticated
 * unconditionally and must never re-ask the SDK. Rendering children only
 * after store and SDK agree closes the one-effect-lag window that caused the
 * warm-session "/ → /login → marketplace" flash (guards reading the store
 * one commit before the SDK's answer landed).
 */
export function AuthGate({ children }) {
  const kindeAuth = useKindeAuth()
  const { isLoading, isAuthenticated } = kindeAuth
  const storeAuthenticated = useAuthStore((s) => s.isAuthenticated)

  useEffect(() => {
    // Keep authBridge connected so apiClient can get fresh tokens
    authBridge.setKindeAuth(kindeAuth)
  })

  useEffect(() => {
    // Sync the SDK's decision into the store — only after the SDK settles
    if (!isLoading) {
      useAuthStore.getState().setAuthenticated(isAuthenticated)
    }
  }, [isLoading, isAuthenticated])

  if (isLoading || storeAuthenticated !== isAuthenticated) {
    return <LoadingScreen />
  }

  return children
}

AuthGate.propTypes = {
  children: PropTypes.node.isRequired,
}
